import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { getGroomingSearchPageUrl } from '@rover/react-lib/src/pages/grooming/utils/params';
import {
  getTrainerSearchPageUrl,
  parseCoordinate,
} from '@rover/react-lib/src/pages/training/utils/params';
import getSearchUrl from '@rover/react-lib/src/utils/getSearchUrl';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import { ServiceTypeSlug } from '@rover/types/src/ServiceType';

export type CrossVerticalSearchLocation = Partial<
  Pick<SearchFilters, 'location' | 'centerlat' | 'centerlng' | 'locationType' | 'rawLocationTypes'>
>;

export type BuildCrossVerticalSearchUrlOptions = {
  serviceTypeSlug: ServiceTypeSlug;
  /**
   * While training and grooming search are untranslated and US-only, the locale is still
   * required by core's getSearchUrl, so we require the caller to provider it for forward
   * compatibility.
   */
  locale: Locale;
  location?: CrossVerticalSearchLocation;
};

/**
 * Builds the URL of the search page that owns the given service type, for navigating
 * between the core, training, and grooming search pages.
 *
 * Existing filters are deliberately not preserved across these pages, since the three
 * pages represent search locations in ways that don't map cleanly onto each other. The
 * only exceptions are:
 *
 * 1. The `location` argument, which callers pass only when the user entered a location
 *    intended for the destination page from the core search page's Search Wizard Modal.
 * 2. Links to core, which carry the selected `service_type` so core displays the right
 *    results, and `bypass_interstitial` so core suppresses its Search Wizard Modal.
 */
const buildCrossVerticalSearchUrl = ({
  serviceTypeSlug,
  locale,
  location = {},
}: BuildCrossVerticalSearchUrlOptions): string => {
  const { location: locationName, centerlat, centerlng, locationType, rawLocationTypes } = location;

  switch (serviceTypeSlug) {
    case ServiceTypeSlug.DOG_TRAINING:
      return getTrainerSearchPageUrl({
        location: locationName,
        centerlat: parseCoordinate(centerlat),
        centerlng: parseCoordinate(centerlng),
        locationType,
        rawLocationTypes,
      });
    case ServiceTypeSlug.GROOMING:
      // The grooming search page has no equivalent of `rawLocationTypes`.
      return getGroomingSearchPageUrl({
        location: locationName,
        centerlat,
        centerlng,
        locationType,
      });
    default:
      return getSearchUrl({ serviceType: serviceTypeSlug, bypassInterstitial: true }, locale);
  }
};

export default buildCrossVerticalSearchUrl;
